import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  Audio,
  Img,
  staticFile,
  random,
} from "remotion";
import {SPRINGS} from "../presets/editing-styles";
import {FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {GradientText} from "../components/dimus/GradientText";
import {SceneTransition} from "../components/dimus/SceneTransition";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * VERDADE DIFÍCIL #06 — "Nunca Funcionou"
 * Editing Style: CINEMATIC
 * Profile: @guilhermeribeiro.ai (foto real do Instagram)
 * Copy: BoardCopy (Halbert + Sugarman + Hormozi)
 * Audio: ElevenLabs TTS per-scene, minimal padding (15f)
 */

const RED = "#F87171";
const RED_DARK = "#1a0505";
const AMBER = "#f5ad43";
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";
const GREEN = "#00DF82";
const CYAN = "#42e9f5";

const SZ = SAFE_ZONES.universal;

// Audio timing — synced to ElevenLabs output + 15f minimal padding
const SCENES_CONFIG = [
  {dur: 98, file: "audio/verdade06/01-hook.mp3", dir: "right" as const},
  {dur: 223, file: "audio/verdade06/02-revelacao.mp3", dir: "left" as const},
  {dur: 406, file: "audio/verdade06/03-evidencia.mp3", dir: "up" as const},
  {dur: 294, file: "audio/verdade06/04-virada.mp3", dir: "right" as const},
  {dur: 207, file: "audio/verdade06/05-cta.mp3", dir: "up" as const},
];

// ─── Ambient particles ────────────────────────────────────────────────
const Particles: React.FC<{count: number; color: string}> = ({count, color}) => {
  const frame = useCurrentFrame();
  return (
    <>
      {Array.from({length: count}, (_, i) => {
        const x = random(`px-${i}`) * 1080;
        const baseY = random(`py-${i}`) * 1920;
        const size = 2 + random(`ps-${i}`) * 3;
        const speed = 0.2 + random(`psp-${i}`) * 0.4;
        const wrapped = ((baseY - frame * speed) % 2100 + 2100) % 2100 - 100;
        return (
          <div key={i} style={{
            position: "absolute", left: x, top: wrapped,
            width: size, height: size, borderRadius: "50%",
            background: color, opacity: 0.08 + random(`po-${i}`) * 0.12,
          }} />
        );
      })}
    </>
  );
};

// ─── Glitch reveal ────────────────────────────────────────────────────
const GlitchReveal: React.FC<{
  before: string; after: string; glitchAt: number; glitchLen: number;
}> = ({before, after, glitchAt, glitchLen}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const isGlitch = frame >= glitchAt && frame < glitchAt + glitchLen;
  const done = frame >= glitchAt + glitchLen;
  const entryP = spring({frame, fps, config: SPRINGS.heavy});
  const revealP = done ? spring({frame: frame - glitchAt - glitchLen, fps, config: SPRINGS.bouncy}) : 0;
  const scale = done ? interpolate(revealP, [0, 0.4, 1], [1.15, 1.15, 1], CLAMP) : 1;
  const jX = isGlitch ? (random(`jx-${frame}`) - 0.5) * 14 : 0;
  const jY = isGlitch ? (random(`jy-${frame}`) - 0.5) * 6 : 0;
  const splitX = isGlitch ? Math.sin(frame * 2.5) * 10 : 0;
  const text = done ? after : before;
  const color = done ? RED : WHITE;
  const fs = FONT_SCALE.h1.min;

  return (
    <div style={{opacity: entryP, transform: `translate(${jX}px,${jY}px) scale(${scale})`, textAlign: "center"}}>
      {isGlitch && <>
        <div style={{position: "absolute", inset: 0, fontSize: fs, fontWeight: 900, color: "rgba(255,50,50,0.6)", fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.1, transform: `translateX(${splitX}px)`, mixBlendMode: "screen"}}>{before}</div>
        <div style={{position: "absolute", inset: 0, fontSize: fs, fontWeight: 900, color: "rgba(50,100,255,0.5)", fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.1, transform: `translateX(${-splitX}px)`, mixBlendMode: "screen"}}>{before}</div>
      </>}
      <div style={{fontSize: fs, fontWeight: 900, color, fontFamily: FONT_STACK, lineHeight: 1.1, textShadow: done ? `0 0 40px rgba(248,113,113,0.5)` : "none"}}>{text}</div>
    </div>
  );
};

// ─── Disintegrating pill ──────────────────────────────────────────────
const Pill: React.FC<{text: string; delay: number; destroyAt: number}> = ({text, delay, destroyAt}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const enterP = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  const destroying = frame >= destroyAt;
  const dProg = destroying ? interpolate(frame, [destroyAt, destroyAt + 15], [0, 1], CLAMP) : 0;

  if (frame < delay) return null;
  return (
    <div style={{position: "relative", display: "inline-block"}}>
      <div style={{
        opacity: enterP * (1 - dProg),
        transform: `translateY(${interpolate(enterP, [0, 1], [25, 0], CLAMP)}px) scale(${1 - dProg * 0.3})`,
        background: "rgba(248,113,113,0.15)", border: "1px solid rgba(248,113,113,0.3)",
        borderRadius: 999, padding: "16px 36px",
        fontSize: FONT_SCALE.body.min, fontWeight: 600, color: WHITE, fontFamily: FONT_STACK,
        filter: destroying ? `blur(${dProg * 3}px)` : "none",
      }}>{text}</div>
      {destroying && Array.from({length: 12}, (_, i) => {
        const a = (i / 12) * Math.PI * 2 + random(`da-${i}`) * 0.5;
        const sp = 3 + random(`ds-${i}`) * 5;
        const lf = frame - destroyAt;
        return <div key={i} style={{
          position: "absolute", left: "50%", top: "50%",
          width: 4 + random(`dsz-${i}`) * 4, height: 3, borderRadius: 2,
          background: RED, opacity: interpolate(lf, [0, 15], [0.9, 0], CLAMP),
          transform: `translate(${Math.cos(a) * sp * lf * 0.8}px, ${Math.sin(a) * sp * lf * 0.5 + lf * lf * 0.01}px)`,
        }} />;
      })}
    </div>
  );
};

// ─── SCENE 1: HOOK ────────────────────────────────────────────────────
const S1: React.FC = () => (
  <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
    <Audio src={staticFile(SCENES_CONFIG[0].file)} volume={0.9} />
    <GradientText text="VERDADE #06" colors={[RED, AMBER]} fontSize={FONT_SCALE.h1.max} springProfile="bouncy" />
  </AbsoluteFill>
);

// ─── SCENE 2: REVELAÇÃO ──────────────────────────────────────────────
const S2: React.FC = () => (
  <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left + 10}px ${SZ.bottom}px`}}>
    <Audio src={staticFile(SCENES_CONFIG[1].file)} volume={0.9} />
    <GlitchReveal before="PAROU DE FUNCIONAR" after="NUNCA FUNCIONOU" glitchAt={110} glitchLen={18} />
  </AbsoluteFill>
);

// ─── SCENE 3: EVIDÊNCIA ──────────────────────────────────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const introP = spring({frame, fps, config: SPRINGS.smooth});
  const closerP = spring({frame: frame - 250, fps, config: SPRINGS.heavy});

  return (
    <OrganicLayer intensity="subtle" seed="verdade-06">
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES_CONFIG[2].file)} volume={0.9} />
      <div style={{opacity: introP, fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK, textAlign: "center"}}>
        Esses canais sustentavam teu faturamento:
      </div>
      <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 14}}>
        <Pill text="Indicação de amigo" delay={30} destroyAt={280} />
        <Pill text="Recompra de cliente antigo" delay={55} destroyAt={295} />
        <Pill text="Um vendedor bom" delay={80} destroyAt={310} />
      </div>
      <div style={{
        opacity: closerP,
        transform: `translateY(${interpolate(closerP, [0, 1], [20, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.body.min, fontWeight: 700, color: RED, fontFamily: FONT_STACK,
        textAlign: "center", lineHeight: 1.3, maxWidth: "90%", marginTop: 12,
      }}>
        "Parou de funcionar."
      </div>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 4: VIRADA ─────────────────────────────────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 5, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 50, fps, config: SPRINGS.smooth});
  const p3 = spring({frame: frame - 130, fps, config: SPRINGS.bouncy});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 16}}>
      <Audio src={staticFile(SCENES_CONFIG[3].file)} volume={0.9} />
      <div style={{opacity: p1, transform: `translateX(${interpolate(p1, [0, 1], [-40, 0], CLAMP)}px)`, fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>
        Não parou. Nunca começou.
      </div>
      <div style={{opacity: p2, fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK, lineHeight: 1.4, maxWidth: "92%"}}>
        Você tinha presença. Postava, aparecia, investia.
      </div>
      <div style={{display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap", marginTop: 8}}>
        <GradientText text="Presença" colors={[RED, "#ff9999"]} fontSize={FONT_SCALE.h1.min} delay={100} springProfile="bouncy" />
        <div style={{opacity: p3, fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>sem</div>
        <GradientText text="sistema" colors={[GREEN, CYAN]} fontSize={FONT_SCALE.h1.min} delay={120} springProfile="bouncy" />
      </div>
      <div style={{opacity: p3, fontSize: FONT_SCALE.body.min, fontWeight: 800, color: AMBER, fontFamily: FONT_STACK, marginTop: 8}}>
        é barulho organizado.
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 5: CTA com foto real ──────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const profileP = spring({frame: frame - 8, fps, config: SPRINGS.snappy});
  const btnP = spring({frame: frame - 50, fps, config: SPRINGS.bouncy});
  const pulse = frame > 100 ? 1 + Math.sin((frame - 100) * 0.08) * 0.02 : 1;

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", gap: 20, flexDirection: "column", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(SCENES_CONFIG[4].file)} volume={0.9} />
      {/* Teaser text */}
      <div style={{
        opacity: profileP, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3,
        transform: `translateY(${interpolate(profileP, [0, 1], [15, 0], CLAMP)}px)`,
      }}>
        Na próxima: presença vs sistema.
      </div>
      {/* Profile with REAL photo */}
      <div style={{
        opacity: profileP,
        transform: `translateY(${interpolate(profileP, [0, 1], [20, 0], CLAMP)}px)`,
        display: "flex", alignItems: "center", gap: 16,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", overflow: "hidden",
          border: `3px solid ${RED}`,
          boxShadow: `0 0 20px rgba(248,113,113,0.3)`,
        }}>
          <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
            style={{width: "100%", height: "100%", objectFit: "cover"}} />
        </div>
        <div>
          <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>
            @guilhermeribeiro.ai
          </div>
          <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>
            Crescimento previsível
          </div>
        </div>
      </div>
      {/* CTA button */}
      <div style={{
        opacity: btnP,
        transform: `translateY(${interpolate(btnP, [0, 1], [30, 0], CLAMP)}px) scale(${interpolate(btnP, [0, 1], [0.5, 1], CLAMP) * pulse})`,
        padding: "18px 52px", borderRadius: 999, background: RED,
        fontSize: FONT_SCALE.cta.max, fontWeight: 700, color: "#000", fontFamily: FONT_STACK,
        boxShadow: `0 0 40px rgba(248,113,113,0.4)`,
      }}>
        SALVA E SEGUE
      </div>
    </AbsoluteFill>
  );
};

// ─── MAIN COMPOSITION ─────────────────────────────────────────────────
export const VERDADE06_TOTAL = SCENES_CONFIG.reduce((s, sc) => s + sc.dur, 0);

export const Verdade06: React.FC = () => {
  let offset = 0;
  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 35%, ${RED_DARK}, #000 70%)`}} />
      <Noise opacity={0.02} />
      <Particles count={30} color={RED} />
      <FilmGrain opacity={0.04} />
      <Vignette intensity={0.7} />

      {SCENES_CONFIG.map(({dur, dir}, i) => {
        const from = offset;
        offset += dur;
        const Comp = [S1, S2, S3, S4, S5][i];
        return (
          <Sequence key={i} from={from} durationInFrames={dur}>
            <SceneFade duration={dur}>
              <SceneTransition type="in" direction={dir} springProfile="smooth">
                <Comp />
              </SceneTransition>
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 8], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 10, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
